import { AgentPresence } from './agentLiveView'
// Use enums for better autocompletion of action type names. These will
// be compiled away leaving only the final value in your compiled code.
//
// Define however naming conventions you'd like for your action types, but
// personally, I use the `@@context/ACTION_TYPE` convention, to follow the convention

import { StatusInternalTests } from 'common/types/internalTests'
import { NotificationLevels } from 'store/notifier/types'
import {
  UploaderParams,
  SocketInstanceResources,
  OpenAppParams,
  PendingAsyncOperation,
  AsyncOperationType,
  AsyncOperationProgress
} from './constants'

// of Redux's `@@INIT` action.
export enum SocketActionTypes {
  SEND_MESSAGE = '@@socket/SEND_MESSAGE',
  START_SERVER = '@@socket/START_SERVER',
  STOP_SERVER = '@@socket/STOP_SERVER',
  WEBSOCKET_CONNECTION_STATUS_ON = '@@socket/WEBSOCKET_CONNECTION_STATUS_ON',
  WEBSOCKET_CONNECTION_STATUS_OFF = '@@socket/WEBSOCKET_CONNECTION_STATUS_OFF',
  SET_WEBSOCKET_PREVIOUS_CONNECTION_STATUS = '@@socket/SET_WEBSOCKET_PREVIOUS_CONNECTION_STATUS',
  SET_PROGRESS = '@@socket/SET_PROGRESS',
  SET_PROGRESSBAR = '@@socket/SET_PROGRESSBAR',
  UPDATE_INSTANCE_RESOURCES = '@@socket/UPDATE_INSTANCE_RESOURCES',
  HIDE_PROGRESSBAR = '@@socket/HIDE_PROGRESSBAR',
  SET_UPLOADER_PARAMS = '@@socket/SET_UPLOADER_PARAMS',
  INTERNAL_TESTS_STATUS = '@@socket/INTERNAL_TESTS_STATUS',
  RESET_INTERNAL_TESTS = '@@socket/RESET_INTERNAL_TESTS',
  SET_APP_LOCK_INFO = '@@socket/SET_APP_LOCK_INFO',
  SET_REFRESH_APP_NOTIFICATIONS = '@@socket/SET_REFRESH_APP_NOTIFICATIONS',
  SET_SCHEDULED_TASK_UPDATE = '@@socket/SET_SCHEDULED_TASK_UPDATE',
  TRANSLATIONS_PROGRESS = '@@socket/TRANSLATIONS_PROGRESS',
  RESET_TRANSLATIONS_PROGRESS = '@@socket/RESET_TRANSLATIONS_PROGRESS',
  OPEN_APP_WITH_UNCONFIRMED_CHANGES = '@@socket/OPEN_APP_WITH_UNCONFIRMED_CHANGES',
  ADD_PENDING_FILE_OPERATION = '@@socket/ADD_PENDING_FILE_OPERATION',
  REMOVE_PENDING_FILE_OPERATION = '@@socket/REMOVE_PENDING_FILE_OPERATION',
  SET_FILE_OPERATION_RESULT = '@@socket/SET_FILE_OPERATION_RESULT',
  CLEAR_FILE_OPERATION_RESULT = '@@socket/CLEAR_FILE_OPERATION_RESULT',
  SET_ASYNC_OPERATION_PROGRESS = '@@socket/SET_ASYNC_OPERATION_PROGRESS',
  CLEAR_ASYNC_OPERATION_PROGRESS = '@@socket/CLEAR_ASYNC_OPERATION_PROGRESS',
  SET_PERSISTENT_OPERATION = '@@socket/SET_PERSISTENT_OPERATION',
  CLEAR_PERSISTENT_OPERATION = '@@socket/CLEAR_PERSISTENT_OPERATION',
  START_OPERATION_STATUS_POLLING = '@@socket/START_OPERATION_STATUS_POLLING',
  STOP_OPERATION_STATUS_POLLING = '@@socket/STOP_OPERATION_STATUS_POLLING',
  SET_FORCE_STOP_PHASE = '@@socket/SET_FORCE_STOP_PHASE',
  OPEN_APP_TASK_RESULT = '@@socket/OPEN_APP_TASK_RESULT',
  SET_AGENT_PRESENCE = '@@socket/SET_AGENT_PRESENCE',
  SET_FOLLOW_AGENT = '@@socket/SET_FOLLOW_AGENT'
}

export interface SocketProgressState {
  title?: string
  message?: string
  show?: boolean
  value: number
  closeWhenFinished?: boolean
  notLevel?: NotificationLevels
}

export enum WebSocketConnectionStatus {
  ON = 'on',
  OFF = 'off',
  UNKNOWN = 'unknown'
}

export interface AsyncOperationResult {
  operationId: string
  operationType: AsyncOperationType
  success: boolean
  result?: string
  error?: string
}

export interface PersistentOperation {
  operationId: string
  operationType: AsyncOperationType
  startedAt: number
}

// Declare state types with `readonly` modifier to get compile time immutability.
// https://github.com/piotrwitek/react-redux-typescript-guide#state-with-type-level-immutability
/** A scheduled task changed its state. A signal, not the row: whoever cares
 *  refetches through the normal endpoint. `isOpenApp` is resolved per recipient
 *  by the API, so it already answers "does this belong to the app I have open".
 *  `seq` only exists so two identical events in a row still fire an effect. */
export interface ScheduledTaskUpdate {
  taskId: number
  state: string | null
  enabled: boolean
  isOpenApp: boolean
  seq: number
}

export interface SocketState {
  readonly previousWebSocketConnectionStatus?: WebSocketConnectionStatus
  readonly webSocketConnectionStatus?: WebSocketConnectionStatus
  readonly progress?: SocketProgressState
  readonly progressbar?: SocketProgressState
  readonly instanceResources?: SocketInstanceResources
  readonly uploader?: UploaderParams
  readonly internalTests?: StatusInternalTests
  readonly appIsLocked?: boolean
  readonly refreshAppNotifications?: boolean
  readonly scheduledTaskUpdate?: ScheduledTaskUpdate
  readonly translationsProgress?: number
  readonly openAppWithUnconfirmedChanges?: OpenAppParams
  readonly pendingOperations?: Map<string, PendingAsyncOperation>
  readonly lastOperationResult?: AsyncOperationResult
  readonly operationProgress?: AsyncOperationProgress
  readonly persistentOperation?: PersistentOperation
  readonly forceStopPhase?: string | null
  readonly agentPresence?: AgentPresence
  readonly followAgent?: boolean
}
